import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { RefreshCw, Activity, Globe, Sparkles, CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-5" data-testid={`obs-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-4">
        <Icon size={18} className="text-zinc-400" />
        <h3 className="text-sm font-bold text-white uppercase tracking-wider">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, valueClass = 'text-white' }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-zinc-800/50 last:border-0">
      <span className="text-xs text-zinc-400">{label}</span>
      <span className={`text-sm font-semibold ${valueClass}`}>{value}</span>
    </div>
  );
}

export default function Observability() {
  const { getAccessTokenSilently } = useAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(null);

  const load = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${API}/admin/observability`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setData(await r.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [getAccessTokenSilently]);

  useEffect(() => { load(); const int = setInterval(load, 30000); return () => clearInterval(int); }, [load]);

  async function refreshCache(source) {
    setRefreshing(source);
    try {
      const token = await getAccessTokenSilently();
      const r = await fetch(`${API}/admin/observability/refresh?source=${source}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) { toast.success(`Cache ${source} osvježen`); load(); }
      else toast.error('Greška');
    } catch { toast.error('Greška'); }
    finally { setRefreshing(null); }
  }

  if (loading || !data) return (
    <div className="p-10 flex items-center justify-center" data-testid="obs-loading">
      <Loader2 className="animate-spin text-zinc-500" size={24} />
    </div>
  );

  const { scrape_cache, translation, horoscope, subscriptions } = data;
  const fmt = (iso) => iso ? new Date(iso).toLocaleString('hr') : '—';

  return (
    <div className="p-6 space-y-6" data-testid="observability-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Observability</h1>
          <p className="text-sm text-zinc-500">Uvid u cache, prijevode, horoskop i pretplate (zadnja 24h)</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-300 hover:text-white border border-zinc-700 rounded-lg" data-testid="obs-refresh-all">
          <RefreshCw size={14} /> Osvježi
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Scrape Cache */}
        <Card title="Scrape Cache" icon={Activity}>
          {['events', 'sports'].map(kind => {
            const c = scrape_cache[kind];
            return (
              <div key={kind} className="mb-4 last:mb-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-zinc-300 uppercase">{kind}</span>
                  <button onClick={() => refreshCache(kind)} disabled={refreshing === kind}
                          className="text-[11px] text-zinc-400 hover:text-red-400 flex items-center gap-1 disabled:opacity-50" data-testid={`obs-refresh-${kind}`}>
                    {refreshing === kind ? <Loader2 className="animate-spin" size={11} /> : <RefreshCw size={11} />} Osvježi sada
                  </button>
                </div>
                <Row label="Hits 24h" value={c.hits_24h} valueClass="text-emerald-400" />
                <Row label="Misses 24h" value={c.misses_24h} valueClass="text-amber-400" />
                <Row label="Zadnje osvježeno" value={fmt(c.last_refresh)} />
                <Row label="Broj stavki u cacheu" value={c.cached_items} />
                <Row label="Izvor" value={c.source} />
              </div>
            );
          })}
        </Card>

        {/* Translation */}
        <Card title="Google Translate" icon={Globe}>
          <Row label="Pozivi 24h" value={translation.calls_24h} />
          <Row label="Znakovi 24h" value={translation.chars_24h.toLocaleString()} />
          <Row label="Cached hits 24h" value={translation.cached_hits_24h} valueClass="text-emerald-400" />
          <Row label="Cache hit rate" value={`${Math.round(translation.cached_hit_rate * 100)}%`} valueClass="text-emerald-400" />
          <Row label="Greške 24h" value={translation.errors_24h} valueClass={translation.errors_24h > 0 ? 'text-red-400' : 'text-emerald-400'} />
          <Row label="Latencija p50" value={`${translation.recent_latency_ms.p50} ms`} />
          <Row label="Latencija p95" value={`${translation.recent_latency_ms.p95} ms`} />
          {translation.errors_sample.length > 0 && (
            <div className="mt-3 text-[10px] text-red-300">
              <p className="font-semibold mb-1">Primjeri grešaka:</p>
              {translation.errors_sample.slice(0, 3).map((e, i) => <p key={i} className="truncate">• {e}</p>)}
            </div>
          )}
        </Card>

        {/* Horoscope */}
        <Card title="Horoscope" icon={Sparkles}>
          <Row label="Zadnja generacija" value={fmt(horoscope.last_generated_at)} />
          <Row label="Trajanje generacije" value={`${horoscope.last_generation_ms} ms`} />
          <Row label="Horoskopa danas" value={`${horoscope.signs_generated_today} / 12`} valueClass={horoscope.signs_generated_today >= 12 ? 'text-emerald-400' : 'text-amber-400'} />
          <Row label="Jezika prevedeno danas" value={`${horoscope.locales_translated_today} / 11`} />
          <Row label="LLM provider" value={horoscope.llm_provider} />
          <Row label="Greške 24h" value={horoscope.errors_24h} valueClass={horoscope.errors_24h > 0 ? 'text-red-400' : 'text-emerald-400'} />
          <div className="mt-3">
            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase ${horoscope.status === 'ok' ? 'bg-emerald-900/40 text-emerald-400' : 'bg-amber-900/40 text-amber-400'}`}>
              {horoscope.status}
            </span>
          </div>
        </Card>

        {/* Subscriptions */}
        <Card title="Pretplate" icon={CreditCard}>
          <Row label="Aktivne" value={subscriptions.active} valueClass="text-emerald-400" />
          <Row label="Otkazane" value={subscriptions.canceled} valueClass="text-zinc-400" />
          <Row label="Trial" value={subscriptions.trialing} />
          <Row label="MRR (EUR)" value={`€ ${subscriptions.mrr_eur.toFixed(2)}`} valueClass="text-emerald-400 text-lg" />
        </Card>
      </div>
    </div>
  );
}
